// ══════════════════════════════════════════════════════════════════════════════════════════════
//  AL VOLVER A LA APP, SI TOCA FICHAR, A FICHAR (B-873, 26-sep-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «si accedo un Lunes a partir de X tiempo antes de mi horario, entre directamente a la pestaña de Fichar,
//  una vez haya iniciada jornada no vuelve a molestar para nada». Al ENTRAR lo decide la pantalla de inicio (App.jsx ·
//  HomeRedirect). Esto es lo mismo para cuando la app ya estaba abierta y vuelve del fondo: el móvil la deja en segundo
//  plano días enteros, y «abrirla» el lunes es volver a ella, sin pasar por el inicio.
//
//  Lo decide el servidor (GET /control-horario/aterrizaje: su jornada de hoy, la hora, si ya ha entrado). Solo se
//  pregunta si la app ha estado escondida un rato (VOLVER_TRAS_MS): cambiar de pestaña un segundo no es «abrir la app».
//  Y solo se salta de pantalla desde una PORTADA (services/home · esPortada: inicio, agenda, WhatsApp, chat…): en medio
//  de un formulario, un salto perdería lo escrito, y ahí sale este aviso con «Ir a Fichar».
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { esPortada } from '../services/home'
import { t } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const VOLVER_TRAS_MS = 20 * 60 * 1000
// /fichar y lo que cuelga de él (?tab=…), sin confundirlo con otra dirección que empiece igual.
const esRutaFichar = (r) => /^\/fichar(\/|\?|#|$)/.test(String(r || ''))

// Qué hacer con lo que contesta el servidor, sin pantalla: saltar, avisar o nada.
export function queHacerAlVolver({ respuesta, ruta }) {
  if (!respuesta || respuesta.ir !== '/fichar') return 'nada'
  if (esRutaFichar(ruta)) return 'nada'
  return esPortada(ruta) ? 'saltar' : 'avisar'
}

export default function FicharAlVolver() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [aviso, setAviso] = useState(null)
  const escondidaDesde = useRef(null)
  const rutaActual = useRef(pathname)
  rutaActual.current = pathname
  const activo = !!user && user.rol !== 'control_horario'

  useEffect(() => {
    if (!activo || typeof document === 'undefined') return undefined
    const alCambiar = async () => {
      if (document.visibilityState === 'hidden') { escondidaDesde.current = Date.now(); return }
      const desde = escondidaDesde.current
      escondidaDesde.current = null
      if (!desde || Date.now() - desde < VOLVER_TRAS_MS) return
      try {
        const { data } = await api.get('/control-horario/aterrizaje', { timeout: 6000 })
        const que = queHacerAlVolver({ respuesta: data, ruta: rutaActual.current })
        if (que === 'saltar') navigate('/fichar')
        else if (que === 'avisar') setAviso(data)
      } catch { }
    }
    document.addEventListener('visibilitychange', alCambiar)
    return () => document.removeEventListener('visibilitychange', alCambiar)
  }, [activo, navigate])

  useEffect(() => { if (esRutaFichar(pathname)) setAviso(null) }, [pathname])

  if (!aviso) return null
  return (
    <div role="status" data-aviso-fichar style={{
      position: 'fixed', left: 16, right: 16, bottom: 'calc(16px + var(--sa-abajo))', zIndex: 2400, maxWidth: 520, margin: '0 auto',
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
      padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    }}>
      <span style={{ flex: '1 1 220px', minWidth: 0, fontSize: 13.5, color: 'var(--text-primary)', lineHeight: 1.4 }}>
        {/* t172 · con médico a primera hora, el servidor ya da la hora a la que vuelve (y lo marca con tras_ausencia). */}
        {aviso.entrada && aviso.tras_ausencia ? <>{t('components.FicharAlVolver.vuelves_de_tu_ausencia_a', 'Vuelves de tu ausencia a las ')}<b>{aviso.entrada}</b>{t('components.FicharAlVolver.y_todavia_no_has_fichado', ' y todavía no has fichado.')}</>
          : aviso.entrada ? <>{t('components.FicharAlVolver.tu_jornada_empieza_a_las', 'Tu jornada empieza a las ')}<b>{aviso.entrada}</b>{t('components.FicharAlVolver.y_todavia_no_has_fichado', ' y todavía no has fichado.')}</> : <>{t('components.FicharAlVolver.todavia_no_has_fichado_hoy', 'Todavía no has fichado hoy.')}</>}
      </span>
      <button type="button" onClick={() => { setAviso(null); navigate('/fichar') }}
        style={{ padding: '8px 14px', borderRadius: 10, border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
        {t('components.FicharAlVolver.ir_a_fichar', 'Ir a Fichar')}
      </button>
      <button type="button" aria-label={t('components.FicharAlVolver.cerrar_el_aviso', 'Cerrar el aviso')} onClick={() => setAviso(null)}
        style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>
        {t('components.FicharAlVolver.ahora_no', 'Ahora no')}
      </button>
    </div>
  )
}
